import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { SessionDataPoint } from '@totem/components/services/remoteAccess/sessionChart/types';
import colors from '@totem/styles/colors';

type Props = {
  sessionData: SessionDataPoint[];
};

const SessionChart = ({ sessionData }: Props) => {
  const { dataCategories, dataCountSeries, dataAvgDurationSeries, chartKey } =
    useMemo(() => {
      if (!Array.isArray(sessionData) || sessionData.length === 0) {
        return {
          dataCategories: [],
          dataCountSeries: [],
          dataAvgDurationSeries: [],
          chartKey: 'session-chart-empty',
        };
      }

      const chartData = [...sessionData].sort(
        (compA, compB) => compA.date.getTime() - compB.date.getTime(),
      );

      const chartCategories: number[] = [];
      const countSeries: number[] = [];
      const avgDurationSeries: number[] = [];

      for (let idx = 0; idx < chartData.length; idx++) {
        chartCategories.push(chartData[idx].date.getTime());

        countSeries.push(chartData[idx].count);

        avgDurationSeries.push(chartData[idx].avgDurationHours);
      }

      return {
        dataCategories: chartCategories,
        dataCountSeries: countSeries,
        dataAvgDurationSeries: avgDurationSeries,
        chartKey: JSON.stringify({
          categories: chartCategories,
          counts: countSeries,
          avgDurations: avgDurationSeries,
        }),
      };
    }, [sessionData]);

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'line',
      stacked: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: [1, 4],
      curve: 'smooth',
    },
    xaxis: {
      type: 'datetime',
      categories: dataCategories,
      title: {
        text: 'Session Start Date',
        style: {
          fontSize: '14px',
          fontWeight: 500,
        },
      },
    },
    yaxis: [
      {
        axisTicks: {
          show: true,
        },
        axisBorder: {
          show: true,
          color: colors.brand.blue,
        },
        labels: {
          style: {
            colors: colors.brand.blue,
            fontSize: '14px',
            fontWeight: 500,
          },
          formatter: (val) => {
            return val.toFixed(2);
          },
        },
        title: {
          text: 'Number of Sessions',
          style: {
            color: colors.brand.blue,
            fontSize: '14px',
            fontWeight: 500,
          },
        },
        tooltip: {
          enabled: true,
        },
      },
      {
        seriesName: 'Avg Duration (Hours)',
        opposite: true,
        axisTicks: {
          show: true,
        },
        axisBorder: {
          show: true,
          color: '#FEB019',
        },
        labels: {
          style: {
            colors: '#FEB019',
            fontSize: '14px',
            fontWeight: 500,
          },
          formatter: (val) => {
            return val.toFixed(2);
          },
        },
        title: {
          text: 'Average Session Duration (Hours)',
          style: {
            color: '#FEB019',
            fontSize: '14px',
            fontWeight: 500,
          },
        },
      },
    ],
    tooltip: {
      fixed: {
        enabled: true,
        position: 'topLeft', // topRight, topLeft, bottomRight, bottomLeft
        offsetY: 30,
        offsetX: 60,
      },
    },
    legend: {
      horizontalAlign: 'left',
      offsetX: 40,
    },
  };

  const series = [
    {
      name: 'Session Count',
      type: 'column',
      color: colors.brand.blue,
      data: dataCountSeries,
    },
    {
      name: 'Avg Duration (Hours)',
      type: 'line',
      color: '#FEB019',
      data: dataAvgDurationSeries,
    },
  ];

  return (
    <div className="app">
      <div className="row">
        <div className="mixed-chart">
          <ReactApexChart
            key={chartKey}
            options={options}
            series={series}
            type="line"
            width="100%"
            height="300px"
          />
        </div>
      </div>
    </div>
  );
};

export default SessionChart;
